import { dequal } from 'dequal'
import { ROUTES } from '@constants/index'
import { config } from '@config/config'
import { CustomerContext, WCTokens } from '@typesApp/Session'
import { HostEnvironment, HostSession } from '@typesApp/Environment'
import { normalizeBrand } from './normalizeBrand'
import { isCustomerNumberValid } from './isCustomerNumberValid'

type SessionParameters = {
  isLoggingOut: boolean | undefined
  isSessionExpired: boolean | undefined
  session?: HostSession
  currentSession?: HostSession
}

/**
 * Commerce app may use a specific user id for the anonymous user.
 * e.g. N1 sends -1002, but we allow for commerce app to override
 * this value via the session.anonymousUserId configuration.
 */
const DEFAULT_ANONYMOUS_USER_ID = '-1002'

const isGuest = (session: HostSession | undefined): boolean => {
  return session?.isGuest === true
}

export const isAnonymousSession = (session: HostSession | undefined, anonymousUserId?: string): boolean => {
  if (isGuest(session)) return false

  const userId = `${session?.userId ?? ''}`

  // if user id specified, check against anonymous user id
  if (userId) {
    return !isCustomerNumberValid(userId, anonymousUserId)
  }

  // fallback check against session id and cartid
  return !session?.sessionId && !session?.cartId
}

export const sessionValid = (args: SessionParameters): boolean => {
  const { isLoggingOut, isSessionExpired, session, currentSession } = args
  if ((isSessionExpired || isLoggingOut) && currentSession) {
    // consider valid if commerce customer number has changed
    if (session?.userId && session.userId !== currentSession.userId) {
      return false
    }
  }

  const isGuestUser = isGuest(session)
  const hasSessionId = Boolean(session?.sessionId)
  const isAuthenticated = Boolean(session?.isLoggedIn) || Boolean(session?.isPartiallyAuthenticated)

  // session id maybe optional for some brands
  const disableSessionIdCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck

  // authenticated user
  if (isAuthenticated) {
    return disableSessionIdCheck ? true : hasSessionId
  }

  // unauthenticated user
  return disableSessionIdCheck ? isGuestUser : hasSessionId
}

type InitContextFunction = (tokens: WCTokens) => Promise<CustomerContext>

/**
 * Workaround for CLE integration where an empty sessionId but everything else is good
 */
export const hasWCTokensWithoutSessionId = (session: HostSession | undefined): boolean => {
  if (!session) return false
  if (!config.getFeatures()?.allowWCTokensWithoutSessionId) return false

  const isAuthenticated = Boolean(session?.isLoggedIn) || Boolean(session?.isPartiallyAuthenticated)
  const hasTokenOnly = Boolean(!session?.sessionId && session?.wcToken && session?.wcTrustedToken)
  return isAuthenticated && hasTokenOnly
}

/**
 * Workaround for CLE integration where an empty sessionId but everything else is good
 */
export const getCurrentSessionIdOrFetchWithWCTokens = async (
  session: HostSession,
  currentSession: HostSession | undefined,
  initContext: InitContextFunction
): Promise<string> => {
  if (currentSession?.sessionId) {
    return currentSession.sessionId
  } else {
    // need to fetch context explicitly using the wc tokens
    const { wcToken, wcTrustedToken, isPartiallyAuthenticated } = session
    const context = await initContext({ wcToken, wcTrustedToken, isPartiallyAuthenticated })
    return context.sessionId
  }
}

export const isGuestSessionWithUserId = (session: HostSession | undefined): boolean => {
  return isGuest(session) && Boolean(session?.userId)
}

/**
 * Normalize the session objects then check for equality ignoring userId
 */
export const isSessionChanged = (
  source: HostSession | undefined,
  target: HostSession | undefined,
  anonymousUserId?: string
): boolean => {
  const normalizedSource = normalizeHostSession(source, anonymousUserId, true)
  const normalizedTarget = normalizeHostSession(target, anonymousUserId, true)
  return !dequal(normalizedSource, normalizedTarget)
}

/**
 * Normalize session attributes to ensure they are correctly formatted.
 * userId attribute is dropped if removeUserId = true as it is not stored with session object in redux state.
 */
export const normalizeHostSession = (
  session: HostSession | undefined,
  anonymousUserId?: string,
  removeUserId?: boolean
): HostSession | undefined => {
  if (!session) return
  const { userId, wcToken, wcTrustedToken } = session

  const normalizedSession: HostSession = {
    userId: `${userId ?? ''}`,
    isGuest: Boolean(session?.isGuest),
    isLoggedIn: Boolean(session?.isLoggedIn),
    isAnonymous: anonymousUserId
      ? Boolean(isAnonymousSession(session, anonymousUserId))
      : Boolean(session?.isAnonymous),
    isPartiallyAuthenticated: Boolean(session?.isPartiallyAuthenticated),
    cartId: `${session?.cartId ?? ''}`,
    sessionId: `${session?.sessionId ?? ''}`,
    wcToken,
    wcTrustedToken,
  }

  if (!userId || removeUserId) delete normalizedSession.userId
  if (!wcToken) delete normalizedSession.wcToken
  if (!wcTrustedToken) delete normalizedSession.wcTrustedToken

  return normalizedSession
}

export const canRequestAuthToken = (session: HostSession | undefined, anonymousUserId?: string): boolean => {
  if (!session) return false

  // session id maybe optional for some brands
  const disableSessionIdCheck = config.getFeatures()?.disableAuthTokenSessionIdCheck

  const valid = session?.isPartiallyAuthenticated || session?.isLoggedIn || isGuest(session)
  if (disableSessionIdCheck) {
    if (!session?.userId) return valid

    // check user id if provided
    return valid && isCustomerNumberValid(session?.userId, anonymousUserId)
  }

  // check valid session id
  const sessionId = `${session?.sessionId ?? ''}`
  const validWithSessionId = valid && Boolean(sessionId) && sessionId.trim().length > 0
  if (!session?.userId) {
    return validWithSessionId
  }

  // check user id if provided
  return validWithSessionId && isCustomerNumberValid(session?.userId, anonymousUserId)
}

export const getSessionWithCustomerContext = (
  session: HostSession,
  context: CustomerContext | undefined
): HostSession => {
  let sessionCopy = session

  // update redux state with latest session id
  if (context && session.sessionId !== context.sessionId) {
    if (context.sessionId) {
      console.info(
        'directbilling session - using session id %s from server (received from commerce FE was %s)',
        context.sessionId,
        session.sessionId || "''"
      )
    }

    sessionCopy = {
      ...session,
      sessionId: `${context.sessionId ?? ''}`,
      isPartiallyAuthenticated: context.isPartiallyAuthenticated,
    }
  }

  return sessionCopy
}

export const isNewCustomerAfterSessionExpired = (
  hostSession: HostSession,
  currentSession: HostSession | undefined,
  isSessionExpired: boolean | undefined
): boolean => {
  if (!isSessionExpired) return false
  return (
    Boolean(hostSession?.userId) && Boolean(currentSession?.userId) && hostSession.userId !== currentSession?.userId
  )
}

export const isGuestTransitionToAuthenticated = (
  hostSession: HostSession,
  currentSession: HostSession | undefined
): boolean => {
  if (!currentSession) return false

  return Boolean((hostSession.isLoggedIn || hostSession.isPartiallyAuthenticated) && currentSession.isGuest)
}

export const isAuthenticatedTransitionToGuest = (
  hostSession: HostSession,
  currentSession: HostSession | undefined
): boolean => {
  if (!currentSession) return false

  return Boolean((currentSession.isLoggedIn || currentSession.isPartiallyAuthenticated) && hostSession.isGuest)
}

export const getSessionTypeDisplay = (session: HostSession): string => {
  if (session.isGuest) return 'GUEST'
  if (session.isLoggedIn) return 'AUTHENTICATED'
  if (session.isPartiallyAuthenticated) return 'PARTIALLY_AUTHENTICATED'
  if (session.isAnonymous) return 'ANONYMOUS'
  return 'unknown'
}

export const normalizeEnvironment = (environment: HostEnvironment | undefined): HostEnvironment | null => {
  if (!environment) return null

  return {
    ...environment,
    brand: normalizeBrand(environment.brand),
    basePath: getBasePath(),
    basePathSearch: window.location.search ?? '',
    anonymousUserId: `${environment.anonymousUserId ?? DEFAULT_ANONYMOUS_USER_ID}`,
  }
}

const getBasePath = (): string => {
  const routeBase = ROUTES.BASE.replace('/', '')

  // eslint-disable-next-line no-restricted-globals
  let basePath = window.location.pathname
  const pathArray = basePath.split('/')
  if (pathArray.includes(routeBase)) {
    const dbIndex = pathArray.indexOf(routeBase)
    basePath = pathArray.slice(0, dbIndex).join('/')
  }
  return basePath
}

export const isEnvironmentChanged = (
  source: HostEnvironment | undefined,
  target: HostEnvironment | undefined
): boolean => {
  return !dequal(source, target)
}

export const initGlobalLocale = (environment: HostEnvironment) => {
  if (window.directBilling?.env) {
    window.directBilling.env.locale = environment.locale
    window.directBilling.env.currency = environment.currency
  }
}
